import React from 'react';
import { Tag, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import { Price } from '@partsbadger/library';
import { QuoteLineItemProps } from '@partsbadger/types';

import QuoteStore from '../stores/QuoteStore';
import { getMissingSpecs } from '../utils/partStatus';
import { getPricingState } from '../utils/pricingState';
import { PricingFeedback } from './PricingFeedback';

const { Text } = Typography;

interface Props {
    index: number;
    item: QuoteLineItemProps;
    /** Every line item of the variant, one per quantity (default: just `item`). */
    quantityItems?: QuoteLineItemProps[];
}

const formatQuantities = (item: QuoteLineItemProps): number[] => {
    const grouped = Array.isArray(item?.quantities) ? (item.quantities as unknown[]).map(Number).filter(Boolean) : [];
    if (grouped.length > 0) return grouped;
    return item?.quantity ? [Number(item.quantity)] : [];
};

type DisplayedState = 'multiple' | 'priced' | 'select' | 'calculating' | 'manual';

/** What a line item's price area shows, decided in one place for the markup and the test hooks. */
const getPriceView = (item: QuoteLineItemProps) => {
    // Same rule as the expanded card: a price only when the item is genuinely safe to
    // auto-quote (a stale safe_auto_quote must not surface a price after brep-explorer
    // failed, PTB-6278), and "calculating" instead of the manual tag while the analyses
    // that decide the instant price are still running.
    const pricingState = getPricingState(item);
    // Selections still to make (the same rule as the "Pending" tag and Continue): such a part is
    // not priced yet, so the summary asks for them instead of saying a manual quote is required.
    const missingSpecs = getMissingSpecs(QuoteStore.errorFields ?? [], [item.id]);
    const quantities = formatQuantities(item);
    // Which of the branches of the price area is showing.
    const displayedState: DisplayedState =
        quantities.length > 1
            ? 'multiple'
            : pricingState === 'priced'
            ? 'priced'
            : missingSpecs.length
            ? 'select'
            : pricingState === 'calculating'
            ? 'calculating'
            : 'manual';
    return { missingSpecs, quantities, displayedState };
};

/**
 * What the price area shows for one line item. A variant with several quantities is one line item
 * per quantity, so the summary renders this once per line (`showQuantityLine`: always with its own
 * `quantity × unit price`, even for a quantity of one).
 */
const PriceBlock = ({ item, showQuantityLine = false }: { item: QuoteLineItemProps; showQuantityLine?: boolean }) => {
    const { missingSpecs, quantities, displayedState } = getPriceView(item);
    const leadTime = Number(item.lead_time);
    const qty = quantities[0] ? (
        <span
            className="line-item-summary-detail"
            data-quantity={quantities[0]}
            data-testid="line-item-summary-quantity"
        >
            Qty {quantities[0]}
        </span>
    ) : null;

    return (
        <>
            {displayedState === 'multiple' ? (
                <>
                    <span className="line-item-summary-status">{quantities.length} quantities</span>
                    <span className="line-item-summary-detail" data-testid="line-item-summary-quantities">
                        Qty {quantities.join(' · ')}
                    </span>
                    <span className="line-item-summary-detail">Expand to see each price</span>
                </>
            ) : displayedState === 'priced' ? (
                <>
                    <span className="line-item-summary-total">
                        <Price value={item.total_price} />
                    </span>
                    {quantities[0] > 1 || showQuantityLine ? (
                        <span
                            className="line-item-summary-detail"
                            data-quantity={quantities[0]}
                            data-testid="line-item-summary-quantity"
                        >
                            {quantities[0]} × <Price value={item.unit_price} />
                        </span>
                    ) : null}
                    {leadTime > 0 ? (
                        <span className="line-item-summary-detail">
                            {leadTime} business day{leadTime === 1 ? '' : 's'}
                            {item.delivery ? (
                                <Tag
                                    className="line-item-summary-delivery"
                                    data-delivery={item.delivery}
                                    data-testid="line-item-summary-delivery"
                                    color={item.delivery === 'Expedited' ? 'blue' : 'green'}
                                >
                                    {item.delivery}
                                </Tag>
                            ) : null}
                            {!item.is_fastlane_safe ? (
                                <PricingFeedback lineItemId={item.id} variant="no-expedite" />
                            ) : null}
                            {!item.is_standard_safe ? (
                                <PricingFeedback lineItemId={item.id} variant="no-standard" />
                            ) : null}
                        </span>
                    ) : null}
                </>
            ) : displayedState === 'select' ? (
                <>
                    <span>
                        <Tag color="orange" data-testid="line-item-summary-pending">
                            {`Select ${missingSpecs.join(', ')}`}
                        </Tag>
                        <PricingFeedback lineItemId={item.id} />
                    </span>
                    {qty}
                </>
            ) : displayedState === 'calculating' ? (
                <>
                    <span className="line-item-summary-status" data-testid="line-item-summary-calculating">
                        <LoadingOutlined spin /> Calculating price…
                    </span>
                    {qty}
                </>
            ) : (
                <>
                    <span>
                        <Tag className="line-item-summary-manual" color="warning">
                            Manual quote required
                        </Tag>
                        <PricingFeedback lineItemId={item.id} />
                    </span>
                    {qty}
                </>
            )}
        </>
    );
};

export const LineItemSummary = ({ index, item, quantityItems }: Props) => {
    const { product } = item || {};
    const rows = quantityItems && quantityItems.length > 1 ? quantityItems : null;
    const view = getPriceView(item);
    const specs: Array<[string, React.ReactNode]> = [
        ...(item.manufacturing_type
            ? ([['Process', item.manufacturing_type]] as Array<[string, React.ReactNode]>)
            : []),
        ['Material', product?.material],
        ['Finish', product?.finishes],
        ['Coating', product?.coatings],
    ];

    return (
        <div
            className="line-item-summary"
            data-line-item-id={item.id}
            data-pricing-state={rows ? 'multiple' : view.displayedState}
            data-quantities={(rows ? rows.map(entry => entry.quantity) : view.quantities).join(',')}
            data-testid="line-item-summary"
        >
            <div className="line-item-summary-main">
                <Text strong className="line-item-summary-title">
                    Variant {index + 1}
                </Text>
                <dl className="line-item-summary-specs">
                    {specs.map(([label, value]) => (
                        <div key={label} className="line-item-summary-spec">
                            <dt>{label}</dt>
                            <dd>{value || '—'}</dd>
                        </div>
                    ))}
                </dl>
            </div>

            <div className="line-item-summary-price" data-testid="line-item-summary-price">
                {rows ? (
                    rows.map(entry => (
                        <div className="line-item-summary-row" data-testid="line-item-summary-row" key={entry.id}>
                            <PriceBlock item={entry} showQuantityLine />
                        </div>
                    ))
                ) : (
                    <PriceBlock item={item} />
                )}
            </div>
        </div>
    );
};

export default LineItemSummary;
